import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  UserCog,
  Lock,
  Mail,
  Home,
  Loader2,
  LogOut,
  BookOpen,
  Users,
  BarChart3,
  ClipboardList,
  GraduationCap,
  TrendingUp,
  Award,
  Brain,
  FileText,
  PenTool,
  Target,
  Monitor
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_academia-sabeneval/artifacts/ai0i1vi4_2F29CCC3-E812-45AF-8948-674464C4314C.PNG";

const MODULE_ICONS = { BookOpen, Brain, FileText, PenTool, Target, Monitor, Award };

export default function ProfesorPage() {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [professor, setProfessor] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [logging, setLogging] = useState(false);
  
  // Tab state
  const [activeTab, setActiveTab] = useState("dashboard");
  
  // Data states
  const [stats, setStats] = useState(null);
  const [students, setStudents] = useState([]);
  const [simulatorResults, setSimulatorResults] = useState([]);
  const [examResults, setExamResults] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const savedProf = sessionStorage.getItem("professor");
    if (savedProf) {
      setProfessor(JSON.parse(savedProf));
      setIsAuthenticated(true);
      fetchAllData();
    }
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, studentsRes, simRes, examRes, modulesRes] = await Promise.all([
        axios.get(`${API}/professor/stats`),
        axios.get(`${API}/professor/students`),
        axios.get(`${API}/professor/simulator-results`),
        axios.get(`${API}/professor/exam-results`),
        axios.get(`${API}/modules`)
      ]);
      setStats(statsRes.data);
      setStudents(studentsRes.data.students || []);
      setSimulatorResults(simRes.data.results || []);
      setExamResults(examRes.data.results || []);
      setModules(modulesRes.data.modules || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Completa todos los campos");
      return;
    }
    
    setLogging(true);
    try {
      const response = await axios.post(`${API}/professor/login`, { email, password });
      if (response.data.success) {
        setProfessor(response.data.professor);
        sessionStorage.setItem("professor", JSON.stringify(response.data.professor));
        setIsAuthenticated(true);
        toast.success("¡Bienvenido!");
        fetchAllData();
      }
    } catch (error) {
      toast.error("Credenciales incorrectas");
    } finally {
      setLogging(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("professor");
    setIsAuthenticated(false);
    setProfessor(null);
  };

  const getLevelBadge = (level) => {
    const colors = {
      alto: "bg-green-100 text-green-700",
      intermedio: "bg-yellow-100 text-yellow-700",
      bajo: "bg-red-100 text-red-700"
    };
    return colors[level] || "bg-slate-100 text-slate-700";
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-blue-100" data-testid="professor-login-card">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <UserCog className="w-8 h-8 text-blue-600" />
            </div>
            <CardTitle className="font-heading text-2xl">Portal del Profesor</CardTitle>
            <p className="text-slate-500 text-sm mt-2">Ingresa tus credenciales</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-400" />
                  Correo electrónico
                </Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@email.com" className="h-11" data-testid="professor-email-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-slate-400" />
                  Contraseña
                </Label>
                <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="h-11" data-testid="professor-password-input" />
              </div>
              <Button type="submit" disabled={logging} className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11" data-testid="professor-login-btn">
                {logging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserCog className="w-4 h-4 mr-2" />}
                {logging ? "Ingresando..." : "Ingresar"}
              </Button>
            </form>
            <div className="mt-4 text-center">
              <Button variant="ghost" onClick={() => navigate("/")} className="text-slate-500">
                <Home className="w-4 h-4 mr-2" />
                Volver al inicio
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Professor Dashboard
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 py-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <a href="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Sabeneval" className="h-10 w-auto" />
              <span className="font-heading font-bold text-lg text-purple-700 hidden sm:block">SABENEVAL</span>
            </a>
            <Badge className="bg-blue-100 text-blue-700">Profesor</Badge>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600 hidden sm:block">Hola, <strong>{professor?.name}</strong></span>
            <Button onClick={handleLogout} variant="outline" size="sm" className="text-red-600 border-red-200">
              <LogOut className="w-4 h-4 mr-2" />
              Salir
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-200 pb-2 overflow-x-auto">
          <Button
            variant={activeTab === "dashboard" ? "default" : "ghost"}
            onClick={() => setActiveTab("dashboard")}
            className={activeTab === "dashboard" ? "bg-blue-600" : ""}
            data-testid="tab-dashboard"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
          <Button
            variant={activeTab === "estudiantes" ? "default" : "ghost"}
            onClick={() => setActiveTab("estudiantes")}
            className={activeTab === "estudiantes" ? "bg-blue-600" : ""}
            data-testid="tab-estudiantes"
          >
            <Users className="w-4 h-4 mr-2" />
            Estudiantes
          </Button>
          <Button
            variant={activeTab === "simuladores" ? "default" : "ghost"}
            onClick={() => setActiveTab("simuladores")}
            className={activeTab === "simuladores" ? "bg-blue-600" : ""}
            data-testid="tab-simuladores"
          >
            <ClipboardList className="w-4 h-4 mr-2" />
            Resultados Simuladores
          </Button>
          <Button
            variant={activeTab === "diagnosticos" ? "default" : "ghost"}
            onClick={() => setActiveTab("diagnosticos")}
            className={activeTab === "diagnosticos" ? "bg-blue-600" : ""}
            data-testid="tab-diagnosticos"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Exámenes Diagnósticos
          </Button>
          <Button
            variant={activeTab === "modulos" ? "default" : "ghost"}
            onClick={() => setActiveTab("modulos")}
            className={activeTab === "modulos" ? "bg-blue-600" : ""}
            data-testid="tab-modulos"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            Módulos
          </Button>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        )}

        {/* Dashboard Tab */}
        {activeTab === "dashboard" && !loading && stats && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <GraduationCap className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Estudiantes Activos</p>
                      <p className="text-3xl font-bold text-slate-900">{stats.total_students}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                      <ClipboardList className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Simuladores Realizados</p>
                      <p className="text-3xl font-bold text-slate-900">{stats.total_simulations}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Exámenes Diagnósticos</p>
                      <p className="text-3xl font-bold text-slate-900">{stats.total_exams}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                      <BarChart3 className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Promedio Simuladores</p>
                      <p className="text-3xl font-bold text-slate-900">{stats.avg_simulation_score}%</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                      <Award className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Promedio Diagnósticos</p>
                      <p className="text-3xl font-bold text-slate-900">{stats.avg_exam_score}%</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Summary */}
            <div className="grid md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Últimos Resultados de Simuladores</CardTitle>
                </CardHeader>
                <CardContent>
                  {simulatorResults.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">Sin resultados aún</p>
                  ) : (
                    <div className="space-y-2">
                      {simulatorResults.slice(0, 5).map((result, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium text-sm">{result.student_email || "Anónimo"}</p>
                            <p className="text-xs text-slate-500">{result.simulator_title}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-lg">{result.percentage}%</p>
                            <Badge className={getLevelBadge(result.level)}>{result.level}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Últimos Exámenes Diagnósticos</CardTitle>
                </CardHeader>
                <CardContent>
                  {examResults.length === 0 ? (
                    <p className="text-slate-500 text-center py-4">Sin resultados aún</p>
                  ) : (
                    <div className="space-y-2">
                      {examResults.slice(0, 5).map((result, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium text-sm">Diagnóstico #{idx + 1}</p>
                            <p className="text-xs text-slate-500">{result.total_score}/{result.total_questions} correctas</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-lg">{result.percentage}%</p>
                            <Badge className={getLevelBadge(result.level)}>{result.level}</Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Estudiantes Tab */}
        {activeTab === "estudiantes" && !loading && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Lista de Estudiantes ({students.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {students.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No hay estudiantes con acceso confirmado</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Nombre</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Email</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Universidad</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Carrera</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((student) => (
                        <tr key={student.id} className="border-b hover:bg-slate-50">
                          <td className="px-4 py-3 font-medium">{student.full_name}</td>
                          <td className="px-4 py-3 text-slate-600">{student.email}</td>
                          <td className="px-4 py-3 text-slate-600">{student.university}</td>
                          <td className="px-4 py-3 text-slate-600">{student.career}</td>
                          <td className="px-4 py-3">
                            <Badge className="bg-green-100 text-green-700">Activo</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Simuladores Tab */}
        {activeTab === "simuladores" && !loading && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5" />
                Resultados de Simuladores ({simulatorResults.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {simulatorResults.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No hay resultados de simuladores aún</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Estudiante</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Simulador</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Puntaje</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Porcentaje</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Nivel</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Tiempo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {simulatorResults.map((result, idx) => (
                        <tr key={idx} className="border-b hover:bg-slate-50">
                          <td className="px-4 py-3 font-medium">{result.student_email || "Anónimo"}</td>
                          <td className="px-4 py-3 text-slate-600">{result.simulator_title}</td>
                          <td className="px-4 py-3 text-slate-600">{result.total_score}/{result.total_questions}</td>
                          <td className="px-4 py-3 font-bold">{result.percentage}%</td>
                          <td className="px-4 py-3">
                            <Badge className={getLevelBadge(result.level)}>{result.level}</Badge>
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            {result.time_taken_seconds ? `${Math.floor(result.time_taken_seconds / 60)}:${(result.time_taken_seconds % 60).toString().padStart(2, '0')}` : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Diagnosticos Tab */}
        {activeTab === "diagnosticos" && !loading && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Resultados de Exámenes Diagnósticos ({examResults.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {examResults.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No hay resultados de diagnósticos aún</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">#</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Puntaje</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Porcentaje</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Nivel</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Áreas Fuertes</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Áreas Débiles</th>
                      </tr>
                    </thead>
                    <tbody>
                      {examResults.map((result, idx) => (
                        <tr key={idx} className="border-b hover:bg-slate-50">
                          <td className="px-4 py-3 font-medium">{idx + 1}</td>
                          <td className="px-4 py-3 text-slate-600">{result.total_score}/{result.total_questions}</td>
                          <td className="px-4 py-3 font-bold">{result.percentage}%</td>
                          <td className="px-4 py-3">
                            <Badge className={getLevelBadge(result.level)}>{result.level}</Badge>
                          </td>
                          <td className="px-4 py-3 text-slate-600 text-sm">
                            {result.strong_areas?.join(", ") || "-"}
                          </td>
                          <td className="px-4 py-3 text-slate-600 text-sm">
                            {result.weak_areas?.join(", ") || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Modulos Tab */}
        {activeTab === "modulos" && !loading && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {modules.map((module) => {
              const Icon = MODULE_ICONS[module.icon] || BookOpen;
              return (
                <Card key={module.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${module.color}-100`}>
                        <Icon className={`w-6 h-6 text-${module.color}-600`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900">{module.title}</h3>
                        <p className="text-sm text-slate-500 mt-1">{module.description}</p>
                        <Badge variant="secondary" className="mt-2">Módulo {module.id}</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
